<div class="dashboard-page">
  <header class="dashboard-header">
    <div>
      <span class="eyebrow">Operations overview</span>
      <h1>Welcome back, {{ sharedService.logged_in_user?.name_first || 'Dealer' }}</h1>
      <p>
        {{ (sharedService.company_info_retrieved$ | async)?.company_name || 'Your dealership' }}
        @if (company_number) { <span class="company-number">{{ company_number }}</span> }
      </p>
    </div>
    <button class="refresh-button" type="button" (click)="loadDashboardData()">
      <i class="fa-solid fa-rotate"></i>
      Refresh data
    </button>
  </header>

  <nav class="quick-actions" aria-label="Quick actions">
    <a routerLink="/acquisition"><i class="fa-solid fa-arrow-down"></i><span><span class="button_titles">Receive Firearm</span><small>Add an Acquisition</small></span><i class="fa-solid fa-chevron-right chevron"></i></a>
    <a routerLink="/disposition-selection"><i class="fa-solid fa-arrow-up"></i><span><span class="button_titles">Start Disposition</span><small>Process a Transfer</small></span><i class="fa-solid fa-chevron-right chevron"></i></a>
    <a routerLink="/buyer-form-4473"><i class="fa-solid fa-file-signature"></i><span><span class="button_titles">New Form 4473</span><small>Begin buyer workflow</small></span><i class="fa-solid fa-chevron-right chevron"></i></a>
    <a routerLink="/bound-book"><i class="fa-solid fa-book"></i><span><span class="button_titles">Open Bound Book</span><small>Review inventory</small></span><i class="fa-solid fa-chevron-right chevron"></i></a>
  </nav>

  <section class="metric-grid" aria-label="Key business metrics">
    <article class="metric-card primary">
      <div class="metric-icon"><i class="fa-solid fa-boxes-stacked"></i></div>
      <div><span>Firearms on hand</span><span class="button_titles">{{ on_hand_firearm_count || 0 }}</span><small>Current non-NFA inventory</small></div>
    </article>
    <article class="metric-card">
      <div class="metric-icon"><i class="fa-solid fa-arrow-trend-up"></i></div>
      <div><span>Acquired</span><span class="button_titles">{{ acquired_count_last_7_days || 0 }}</span><small>During the last 7 days</small></div>
    </article>
    <article class="metric-card">
      <div class="metric-icon"><i class="fa-solid fa-arrow-right-from-bracket"></i></div>
      <div><span>Disposed</span><span class="button_titles">{{ disposed_count_last_7_days || 0 }}</span><small>During the last 7 days</small></div>
    </article>
    <article class="metric-card attention" [class.has-alert]="pending_4473_count > 0">
      <div class="metric-icon"><i class="fa-solid fa-file-circle-exclamation"></i></div>
      <div><span>Pending 4473s</span><span class="button_titles">{{ pending_4473_count || 0 }}</span><small>Require attention</small></div>
    </article>
    <article class="metric-card">
      <div class="metric-icon"><i class="fa-solid fa-shield-halved"></i></div>
      <div><span>NFA on hand</span><span class="button_titles">{{ on_hand_nfa_count || 0 }}</span><small>Current NFA inventory</small></div>
    </article>
    <article class="metric-card attention" [class.has-alert]="pending_nfa_count > 0">
      <div class="metric-icon"><i class="fa-solid fa-triangle-exclamation"></i></div>
      <div><span>Pending 3310s</span><span class="button_titles">{{ pending_nfa_count || 0 }}</span><small>Compliance queue</small></div>
    </article>
  </section>

  <section class="dashboard-content">
    <article class="inventory-panel">
      <div class="panel-header">
        <div><span class="panel-kicker">Live inventory</span><h2>Firearms on hand</h2></div>
        <a routerLink="/bound-book">View bound book <i class="fa-solid fa-arrow-right"></i></a>
      </div>

      @if (on_hand_firearm_list.length > 0) {
        <div class="table-scroll">
          <table class="firearms_table_rows">
            <thead><tr><th>Firearm</th><th>Type</th><th>Serial number</th><th>Caliber</th><th>Acquired</th></tr></thead>
            <tbody>
              @for (firearm of on_hand_firearm_list.slice(0, 10); track firearm.firearm_serial_number) {
                <tr>
                  <td><span>{{ firearm.firearm_manufacturer || 'Unknown' }}</span><span>{{ firearm.firearm_model || 'Model not provided' }}</span></td>
                  <td>{{ firearm.firearm_type || '—' }}</td>
                  <td>{{ firearm.firearm_serial_number || '—' }}</td>
                  <td>{{ firearm.firearm_caliber || '—' }}</td>
                  <td>{{ firearm.acquisition_date | date:'MMM d, y' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <div class="empty-state"><i class="fa-solid fa-box-open"></i><span class="button_titles">No firearms on hand</span><p>New acquisitions will appear here automatically.</p><a routerLink="/acquisition">Receive a firearm</a></div>
      }
    </article>

    <aside class="side-stack">
      <article class="status-panel">
        <div class="panel-header"><div><span class="panel-kicker">Compliance</span><h2>Attention needed</h2></div></div>
        <a class="status-row" routerLink="/pending-dispositions"><span class="status-icon"><i class="fa-solid fa-file-circle-exclamation"></i></span><span><span class="button_titles">Pending Form 4473</span><small>Incomplete transfer records</small></span><b>{{ pending_4473_count || 0 }}</b></a>
        <div class="status-row"><span class="status-icon"><i class="fa-solid fa-triangle-exclamation"></i></span><span><span class="button_titles">Pending Form 3310</span><small>Reports awaiting action</small></span><b>{{ pending_nfa_count || 0 }}</b></div>
        <div class="status-row success"><span class="status-icon"><i class="fa-solid fa-circle-check"></i></span><span><span class="button_titles">Data synchronization</span><small>Dashboard records loaded</small></span><b><i class="fa-solid fa-check"></i></b></div>
      </article>

      <article class="nfa-panel">
        <div class="panel-header"><div><span class="panel-kicker">Regulated inventory</span><h2>NFA items</h2></div><span class="button_titles panel-total">{{ on_hand_nfa_count || 0 }}</span></div>
        @if (on_hand_nfa_list.length > 0) {
          <div class="compact-list">
            @for (nfa of on_hand_nfa_list.slice(0, 5); track nfa.firearm_serial_number) {
              <div><span><span class="nfa_table">{{ nfa.firearm_manufacturer }} {{ nfa.firearm_model }}</span><small>{{ nfa.firearm_type }} · {{ nfa.firearm_caliber }}</small></span ><span class="nfa_table">{{ nfa.firearm_serial_number }}</span></div>
            }
          </div>
        } @else {
          <p class="small-empty">No NFA items are currently on hand.</p>
        }
      </article>
    </aside>
  </section>
</div>
